export type CurrencyCode = 'TON' | 'STARS' | 'GIFT';
export type GameCurrencyKey = 'rolls' | 'jetton' | 'mines' | 'balloon';

/** Max number of gifts a user can stake in a single bet. */
export const MAX_GIFTS_PER_BET = 1;

export const CURRENCY_ORDER: CurrencyCode[] = ['TON', 'STARS', 'GIFT'];

export const USD_RATES: Record<CurrencyCode, number> = {
  TON: 5,
  STARS: 0.016,
  GIFT: 0,
};

export const GAME_CURRENCY_LIMITS: Record<
  GameCurrencyKey,
  Record<CurrencyCode, { minBet: number; maxBet: number; maxPayout?: number }>
> = {
  rolls: {
    TON: { minBet: 0.1, maxBet: 100, maxPayout: 100 },
    STARS: { minBet: 50, maxBet: 30000, maxPayout: 30000 },
    GIFT: { minBet: 1, maxBet: MAX_GIFTS_PER_BET },
  },
  jetton: {
    TON: { minBet: 0.1, maxBet: 5, maxPayout: 50 },
    STARS: { minBet: 50, maxBet: 1000, maxPayout: 15000 },
    GIFT: { minBet: 1, maxBet: MAX_GIFTS_PER_BET },
  },
  mines: {
    TON: { minBet: 0.1, maxBet: 5, maxPayout: 100 },
    STARS: { minBet: 50, maxBet: 1000, maxPayout: 20000 },
    GIFT: { minBet: 1, maxBet: MAX_GIFTS_PER_BET },
  },
  balloon: {
    TON: { minBet: 0.1, maxBet: 5, maxPayout: 50 },
    STARS: { minBet: 50, maxBet: 1000, maxPayout: 15000 },
    GIFT: { minBet: 1, maxBet: MAX_GIFTS_PER_BET },
  },
};

export function isCurrencyCode(value: string): value is CurrencyCode {
  return value === 'TON' || value === 'STARS' || value === 'GIFT';
}

/**
 * Widest valid bet range for a currency across all games — the smallest minBet
 * and largest maxBet. Used where bets aren't tied to one game (e.g. shared
 * quick-buy presets); each game still clamps to its own limits at bet time.
 */
export function getCurrencyBetRange(currency: CurrencyCode): { min: number; max: number } {
  const games = Object.values(GAME_CURRENCY_LIMITS);
  return {
    min: Math.min(...games.map((g) => g[currency].minBet)),
    max: Math.max(...games.map((g) => g[currency].maxBet)),
  };
}

export function getCurrencyDecimals(currency: CurrencyCode): number {
  return currency === 'TON' ? 2 : 0;
}

export function getCurrencyStep(currency: CurrencyCode): number {
  return currency === 'TON' ? 0.1 : 1;
}

export function getGameCurrencyLimits(game: GameCurrencyKey, currency: CurrencyCode) {
  return GAME_CURRENCY_LIMITS[game][currency];
}

export function convertToUsd(amount: number, currency: CurrencyCode): number {
  return amount * USD_RATES[currency];
}

export function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export function formatCurrencyAmount(amount: number, currency: CurrencyCode): string {
  return amount.toFixed(getCurrencyDecimals(currency));
}

export function formatCurrencyValue(amount: number, currency: CurrencyCode): string {
  return `${formatCurrencyAmount(amount, currency)} ${currency}`;
}

export function formatCurrencyBreakdown(
  amounts: Partial<Record<CurrencyCode, number>> | Record<string, number>,
): string {
  const parts = CURRENCY_ORDER.flatMap((currency) => {
    const raw = amounts[currency];
    if (raw == null || raw <= 0) return [];
    return formatCurrencyValue(raw, currency);
  });

  return parts.length > 0 ? parts.join(' + ') : '0';
}

export function normalizeAmountForCurrency(value: number, currency: CurrencyCode): number {
  if (currency === 'TON') {
    return Math.round(value * 100) / 100;
  }
  return Math.round(value);
}

/**
 * Floors an amount to the currency's precision. Unlike
 * {@link normalizeAmountForCurrency}, this never rounds up — use it for bet
 * amounts clamped to a balance, so the result can never exceed what the user
 * actually has (which would otherwise fail a `bet <= balance` check).
 */
const compactFormatter = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function formatCompact(value: number): string {
  return compactFormatter.format(value);
}

export function floorAmountForCurrency(value: number, currency: CurrencyCode): number {
  if (currency === 'TON') {
    return Math.floor(value * 100) / 100;
  }
  return Math.floor(value);
}
